import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, MapPin, IndianRupee, Clock, Stethoscope, Send, Filter } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { rupees } from "@/lib/utils-lc";
import { useConfig } from "@/lib/config";
import { track } from "@/lib/track";
import RatingStars from "@/components/RatingStars";

const SPECIALTIES = ["", "General Physician", "Anaesthesia", "Paediatrics", "Emergency", "Cardiology", "Orthopaedics", "OBG", "Radiology", "ICU / Critical Care", "Nurse — General Ward", "Nurse — ICU", "Nurse — OT", "Clinical Assistant", "Alternative Medicine"];

export default function BrowseShifts({ verified }) {
  const { supported_cities } = useConfig();
  const [filters, setFilters] = useState({ specialty: "", date_from: "", date_to: "", min_pay: "", city: "", covers_only: false });
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Note: intentionally does NOT depend on `filters` — refetch is user-driven via the
  // Search button. Filters are read fresh at call time from the setState closure.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.specialty) params.specialty = filters.specialty;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.min_pay) params.min_pay = Number(filters.min_pay);
      if (filters.city) params.city = filters.city;
      if (filters.covers_only) params.posted_by_role = "professional";
      const { data } = await api.get("/shifts", { params });
      setShifts(data);
    } catch (err) {
      if (err.response?.status !== 403) {
        toast.error(err.response?.data?.detail || "Could not load shifts");
      }
      setShifts([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Load once on mount / when the user becomes verified — NOT on every filter edit.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (verified) load(); }, [verified]);

  const apply = async (shift_id) => {
    try {
      await api.post(`/shifts/${shift_id}/apply`);
      track("apply_to_shift", { shift_id });
      toast.success("Applied — the facility will be notified");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not apply");
    }
  };

  if (!verified) {
    return (
      <Card className="rounded-2xl border-slate-200 p-8 text-center bg-white">
        <div className="text-slate-500">Browsing opens up once your credentials are verified.</div>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      <Card className="rounded-2xl border-slate-200 p-5 bg-white">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500 mb-3"><Filter className="w-3.5 h-3.5" /> Filters</div>
        <div className="grid md:grid-cols-5 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5 block">City</Label>
            <Select value={filters.city || "__home"} onValueChange={(v) => setFilters({...filters, city: v === "__home" ? "" : v === "__all" ? "all" : v})}>
              <SelectTrigger data-testid="browse-filter-city" className="h-11 rounded-xl"><SelectValue placeholder="Your city" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__home">Your home city</SelectItem>
                <SelectItem value="__all">All cities</SelectItem>
                {supported_cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5 block">Specialty</Label>
            <Select value={filters.specialty || "__any"} onValueChange={(v) => setFilters({...filters, specialty: v === "__any" ? "" : v})}>
              <SelectTrigger data-testid="browse-filter-specialty" className="h-11 rounded-xl"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__any">Any specialty</SelectItem>
                {SPECIALTIES.filter(Boolean).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5 block">From date</Label>
            <Input data-testid="browse-filter-from" type="date" value={filters.date_from} onChange={e => setFilters({...filters, date_from: e.target.value})} className="h-11 rounded-xl" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5 block">To date</Label>
            <Input data-testid="browse-filter-to" type="date" value={filters.date_to} onChange={e => setFilters({...filters, date_to: e.target.value})} className="h-11 rounded-xl" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5 block">Min pay (₹)</Label>
            <Input data-testid="browse-filter-minpay" type="number" value={filters.min_pay} onChange={e => setFilters({...filters, min_pay: e.target.value})} className="h-11 rounded-xl" placeholder="e.g. 3000" />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 mr-2 cursor-pointer select-none">
            <input
              data-testid="browse-covers-only"
              type="checkbox"
              checked={filters.covers_only}
              onChange={e => setFilters({...filters, covers_only: e.target.checked})}
              className="w-4 h-4 rounded border-slate-300 text-emerald-700 focus:ring-emerald-700"
            />
            Peer covers only
          </label>
          <Button data-testid="browse-search-btn" onClick={load} className="rounded-full bg-emerald-700 hover:bg-emerald-800"><Search className="w-4 h-4 mr-1.5" /> Search</Button>
          <Button data-testid="browse-reset-btn" variant="outline" className="rounded-full" onClick={async () => {
            // Refetch with empty filters immediately (bypassing the memoised `load`
            // which is still bound to pre-reset filters until the next render).
            setFilters({ specialty: "", date_from: "", date_to: "", min_pay: "", city: "", covers_only: false });
            setLoading(true);
            try {
              const { data } = await api.get("/shifts");
              setShifts(data);
            } catch (err) {
              if (err.response?.status !== 403) toast.error(err.response?.data?.detail || "Could not reset");
              setShifts([]);
            } finally { setLoading(false); }
          }}>Reset</Button>
        </div>
      </Card>

      {loading ? (
        <div className="text-sm text-slate-500">Loading shifts…</div>
      ) : shifts.length === 0 ? (
        <Card className="rounded-2xl border-dashed border-slate-300 p-8 text-center text-slate-500 bg-white">No open shifts match your filters right now.</Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {shifts.map(s => (
            <Card key={s.shift_id} className="rounded-2xl border-slate-200 p-5 bg-white hover:shadow-md transition-shadow" data-testid={`shift-card-${s.shift_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-emerald-800"><Stethoscope className="w-3 h-3" /> {s.specialty}</div>
                    {s.posted_by_role === "professional" && <Badge className="bg-indigo-100 text-indigo-800 hover:bg-indigo-100 text-[10px]">Peer cover</Badge>}
                    {s.poster_trust && (
                      <Badge
                        data-testid={`poster-trust-${s.shift_id}`}
                        className={
                          (s.poster_trust.rate_pct >= 30
                            ? "bg-rose-100 text-rose-800 hover:bg-rose-100"
                            : s.poster_trust.rate_pct >= 15
                            ? "bg-amber-100 text-amber-800 hover:bg-amber-100"
                            : "bg-emerald-100 text-emerald-800 hover:bg-emerald-100") + " text-[10px]"
                        }
                        title={`${s.poster_trust.cancelled} of ${s.poster_trust.total_covers} past covers cancelled`}
                      >
                        {s.poster_trust.rate_pct}% cancel · {s.poster_trust.total_covers} covers
                      </Badge>
                    )}
                  </div>
                  <div className="font-display text-lg font-semibold mt-1">{s.facility_name}</div>
                  <RatingStars rating={s.facility_rating} className="mt-0.5" />
                  <div className="text-sm text-slate-600 mt-1">{s.date} · <Clock className="w-3 h-3 inline mb-0.5" /> {s.start_time}–{s.end_time}</div>
                  <div className="text-sm text-slate-600 mt-0.5 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {s.location}{s.city ? ` · ${s.city}` : ""}</div>
                </div>
                <div className="flex items-center gap-1 font-display font-bold text-lg text-slate-900">
                  <IndianRupee className="w-4 h-4" />{Number(s.pay_inr).toLocaleString("en-IN")}
                </div>
              </div>
              {s.notes && <div className="mt-3 text-sm text-slate-500 border-t border-slate-100 pt-3">{s.notes}</div>}
              <div className="mt-4 flex items-center justify-between">
                <span className="text-xs text-slate-500">Pay {rupees(s.pay_inr)} · 10% platform fee</span>
                {s.my_application_status ? (
                  <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">{s.my_application_status}</Badge>
                ) : (
                  <Button size="sm" className="rounded-full bg-emerald-700 hover:bg-emerald-800" onClick={() => apply(s.shift_id)} data-testid={`apply-btn-${s.shift_id}`}>
                    <Send className="w-3.5 h-3.5 mr-1" /> Apply
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
